import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PLANTS, PLANT_FAMILIES } from '@/data/plants';
import PageHeader from '@/components/ui/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useHemisphere } from '@/context/HemisphereContext';
import type { Plant, PlantFamily, BiodynamicDayType, DifficultyLevel } from '@/types/plant';
import './PlantsPage.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DAY_TYPE_COLOR: Record<string, string> = {
  root: 'terracotta', flower: 'rose', fruit: 'ochre', leaf: 'sage',
};

export default function PlantsPage() {
  const { dataMonth } = useHemisphere();
  usePageMeta(
    'Plant Growing Guides',
    'Growing guides for 52 vegetables and herbs — when to sow, transplant and harvest, companion planting, common pests and organic solutions.',
  );
  const [search, setSearch] = useState('');
  const [family, setFamily] = useState<PlantFamily | ''>('');
  const [dayType, setDayType] = useState<BiodynamicDayType | ''>('');
  const [difficulty, setDifficulty] = useState<DifficultyLevel | ''>('');
  const [sowMonth, setSowMonth] = useState<number | ''>('');

  const filtered = useMemo(() => {
    return PLANTS.filter(plant => {
      if (search && !plant.name.toLowerCase().includes(search.toLowerCase()) && !plant.description.toLowerCase().includes(search.toLowerCase())) return false;
      if (family && plant.family !== family) return false;
      if (dayType && plant.biodynamicDay !== dayType) return false;
      if (difficulty && plant.difficulty !== difficulty) return false;
      if (sowMonth !== '') {
        // The reader picks a month in their own calendar; the data is northern.
        const m = dataMonth(sowMonth as number);
        const c = plant.calendar;
        if (!c.sowIndoors?.includes(m) && !c.sowOutdoors?.includes(m) && !c.transplant?.includes(m) && !c.harvest?.includes(m)) return false;
      }
      return true;
    });
  }, [search, family, dayType, difficulty, sowMonth, dataMonth]);

  const clearFilters = () => { setSearch(''); setFamily(''); setDayType(''); setDifficulty(''); setSowMonth(''); };
  const hasFilters = search || family || dayType || difficulty || sowMonth !== '';

  return (
    <div className="plants-page">
      <PageHeader
        title="Plant Database"
        subtitle="Cultivation guides for vegetables, fruits, and herbs — with organic growing advice and biodynamic recommendations."
        eyebrow="Growing knowledge"
        decorationEmoji="🌱"
      />

      <div className="container">
        <div className="plants-layout">

          {/* Filters sidebar */}
          <aside className="plants-filters">
            <div className="filter-group">
              <label className="filter-label text-label">Search</label>
              <input
                className="input"
                type="search"
                placeholder="Tomato, carrot, basil…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label text-label">Plant family</label>
              <select className="select input" value={family} onChange={e => setFamily(e.target.value as PlantFamily | '')}>
                <option value="">All families</option>
                {PLANT_FAMILIES.map(f => (
                  <option key={f.id} value={f.id}>{f.emoji} {f.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label text-label">Biodynamic day</label>
              <div className="filter-pill-group">
                {(['root', 'flower', 'fruit', 'leaf'] as BiodynamicDayType[]).map(dt => (
                  <button
                    key={dt}
                    className={`filter-pill filter-pill--${DAY_TYPE_COLOR[dt]} ${dayType === dt ? 'filter-pill--active' : ''}`}
                    onClick={() => setDayType(dayType === dt ? '' : dt)}
                  >
                    {dt === 'root' ? '🌱' : dt === 'flower' ? '🌸' : dt === 'fruit' ? '🍅' : '🥬'} {dt}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label text-label">Difficulty</label>
              <div className="filter-pill-group">
                {(['easy', 'moderate', 'challenging'] as DifficultyLevel[]).map(d => (
                  <button
                    key={d}
                    className={`filter-pill ${difficulty === d ? 'filter-pill--active-generic' : ''}`}
                    onClick={() => setDifficulty(difficulty === d ? '' : d)}
                  >
                    {d === 'easy' ? '😊' : d === 'moderate' ? '🤔' : '💪'} {d}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label text-label">Active in month</label>
              <select className="select input" value={sowMonth} onChange={e => setSowMonth(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Any month</option>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>

            {hasFilters && (
              <button className="filter-clear text-small" onClick={clearFilters}>
                Clear all filters ×
              </button>
            )}

          </aside>

          {/* Plant grid */}
          <div className="plants-main">
            <div className="plants-results-bar">
              <p className="text-small" style={{ color: 'var(--ink-faint)' }}>
                {filtered.length} plant{filtered.length !== 1 ? 's' : ''}{hasFilters ? ' found' : ' in database'}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="plants-empty">
                <span style={{ fontSize: '3rem' }}>🌿</span>
                <p className="text-subtitle">No plants match your filters</p>
                <button className="filter-clear" onClick={clearFilters}>Clear filters</button>
              </div>
            ) : (
              <div className="plants-grid">
                {filtered.map((plant, i) => (
                  <PlantCard key={plant.id} plant={plant} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PlantCard({ plant, index }: { plant: Plant; index: number }) {
  const { dataMonth } = useHemisphere();
  return (
    <Link
      to={`/plants/${plant.id}`}
      className={`plant-card animate-fade-in-delay-${Math.min(index % 5 + 1, 5)}`}
    >
      {/* Plant image / illustration area */}
      <div className="plant-card-image">
        {plant.imageUrl
          ? <img src={plant.imageUrl} alt={plant.name} className="plant-card-photo" />
          : <span className="plant-card-emoji">{plant.emoji}</span>
        }
        <span className={`plant-card-day-type badge badge-${DAY_TYPE_COLOR[plant.biodynamicDay]}`}>
          {plant.biodynamicDay} day
        </span>
      </div>

      <div className="plant-card-body">
        <div className="plant-card-header">
          <h3 className="plant-card-name text-title">{plant.name}</h3>
          <span className={`plant-card-difficulty difficulty--${plant.difficulty}`}>
            {plant.difficulty === 'easy' ? '😊' : plant.difficulty === 'moderate' ? '🤔' : '💪'}
          </span>
        </div>
        <p className="plant-card-latin text-caption">{plant.latinName}</p>
        <p className="plant-card-desc text-small">{plant.description.slice(0, 110)}…</p>

        {/* Calendar strip */}
        <div className="plant-card-calendar">
          {MONTHS.map((m, i) => {
            const month = dataMonth(i + 1);
            const c = plant.calendar;
            const isSowIn = c.sowIndoors?.includes(month);
            const isSowOut = c.sowOutdoors?.includes(month);
            const isTransplant = c.transplant?.includes(month);
            const isHarvest = c.harvest?.includes(month);
            const type = isHarvest ? 'harvest' : isTransplant ? 'transplant' : isSowIn ? 'sow-in' : isSowOut ? 'sow-out' : '';
            return (
              <div key={m} className={`cal-month ${type ? `cal-month--${type}` : ''}`} title={`${m}${type ? ` — ${type.replace('-', ' ')}` : ''}`}>
                <span className="cal-month-label">{m}</span>
              </div>
            );
          })}
        </div>

        <div className="plant-card-footer">
          <div className="plant-card-tags">
            {plant.tags.slice(0, 3).map(tag => (
              <span key={tag} className="plant-tag text-caption">{tag}</span>
            ))}
          </div>
          <span className="plant-card-arrow">→</span>
        </div>
      </div>
    </Link>
  );
}
